import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, Checklist as CountIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import stockCountService from '../../../../services/stockCountService';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import type { VenueLocation } from '../../../../types/venue';

const todayISO = () => new Date().toISOString().split('T')[0];

const StockCountForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId, venueName } = useVenue();
  const { user } = useAuth();
  const backPath = `/admin/venues/${venueId}/stock-counts`;

  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  // '__none__' is the sentinel value for "no location (venue level)"
  const [scopeValue, setScopeValue] = useState<string>('__none__');
  const [countedAt, setCountedAt] = useState(todayISO());
  const [countedBy, setCountedBy] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setCountedBy((user as any).email ?? (user as any).firstName ?? '');
    }
  }, [user]);

  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        const def = pickDefaultLocation(active);
        if (def) setScopeValue(def._id);
      })
      .catch(() => {})
      .finally(() => setLocationsLoading(false));
  }, [venueId]);

  const handleCreate = async () => {
    setError(null);
    if (!countedAt) {
      setError('Count date is required.');
      return;
    }
    setSaving(true);
    try {
      const locationId = scopeValue === '__none__' ? null : scopeValue;
      const count = await stockCountService.createCount(venueId, {
        locationId,
        countedAt,
        countedBy: countedBy.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      navigate(`/admin/venues/${venueId}/stock-counts/${count._id}`);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to create stock count');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 640 }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            Stock Counts
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CountIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600}>New Stock Count</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Scope selector */}
          <Box>
            <TextField
              select
              fullWidth
              label="Location Scope"
              value={scopeValue}
              onChange={e => setScopeValue(e.target.value)}
              disabled={locationsLoading}
              helperText="A count is scoped to one location. Items from other locations cannot be added."
              size="small"
            >
              <MenuItem value="__none__">
                <Box>
                  <Typography variant="body2" fontWeight={500}>{venueName}</Typography>
                  <Typography variant="caption" color="text.secondary">Venue-level stock · items not assigned to a specific location</Typography>
                </Box>
              </MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc._id} value={loc._id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{loc.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{loc.type}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Date + counted by */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Count Date"
              type="date"
              value={countedAt}
              onChange={e => setCountedAt(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="When the physical count happened"
              required
            />
            <TextField
              label="Counted By"
              value={countedBy}
              onChange={e => setCountedBy(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              placeholder="Name or initials"
            />
          </Box>

          {/* Notes */}
          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Optional"
          />

          {/* Actions */}
          <Box sx={{
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex', gap: 1.5, justifyContent: 'flex-end',
          }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={saving || !countedAt}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Creating…' : 'Create Stock Count'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default StockCountForm;
